/**
 * @file utils.ts
 * @description Stack trace utilities and URL filter matching for the API Visual Debugger.
 * Moved from src/utils/parseStackTrace.ts and extended with filter logic.
 */

import type { DebuggerConfig, StackFrame } from './types';

// ─── Stack Trace Utilities ────────────────────────────────────────────────────

/**
 * Patterns to filter out non-project frames from a stack trace.
 * These are internal Next.js / webpack / Node internals that add noise.
 */
const NOISE_PATTERNS: RegExp[] = [
  // Blanket /node_modules/ is intentionally avoided here.
  // Next.js Turbopack bundles client-side code into paths that contain
  // 'next/static/chunks/node_modules', so a blanket rule would incorrectly
  // drop valid project frames. Instead we target the specific noisy packages.
  /node_modules\/react\//,
  /node_modules\/react-dom\//,
  /node_modules\/scheduler\//,
  /node_modules\/next\//,
  /node_modules\/openapi-fetch\//,
  /webpack-internal/,
  /\(node:/,
  /<anonymous>/,
  /at eval/,
  /next\/dist/,
  /react-dom/,
  /react\/cjs/,
  /at Object\.fetch/,
  /parseStackTrace/,   // exclude this utility itself
  /debugInterceptor/,  // exclude the interceptor bootstrap frames
  /createDebugInterceptor/, // exclude the factory wrapper
  /useApiDebuggerStore/,
  /api-debugger\/store/, // exclude the new module store
  /api-debugger\/utils/, // exclude the new module utils
  /\[root-of-the-server\]/, // filter Turbopack internal async-boundary wrapper frames
  // ── Proxy / Request constructor noise ─────────────────────────────────────
  /Proxy\.(|Request)/,   // frames produced by the Request constructor Proxy
  // ── openapi-fetch internals ────────────────────────────────────────────────
  /coreFetch/,           // openapi-fetch core fetch wrapper
  /fetchMethod/,         // openapi-fetch HTTP-method dispatcher
  // ── openapi-fetch HTTP-method shorthand frames ─────────────────────────────
  /at GET/,
  /at POST/,
  /at PUT/,
  /at DELETE/,
  /at PATCH/,
  /captureStackTrace/,
  /onRequest/,
];

/**
 * Captures the current call stack as a raw string.
 * Call this synchronously — before any await — so the originating component
 * frame is still visible in the stack.
 *
 * Temporarily raises Error.stackTraceLimit to 30 (V8 only) so that frames
 * deep inside Next.js / Turbopack async boundaries are not truncated. The
 * original limit is always restored, even if an error is thrown.
 *
 * ⚠️ PERFORMANCE: This is called synchronously on the request hot path. V8
 * must symbolicate every frame it captures, so keeping the limit modest is
 * important. Previously this was 100, which produced multi-KB stack strings
 * and serialized into visible main-thread stalls when many openapi-fetch
 * calls fired concurrently.
 */
export function captureStackTrace(): string {
  // Error.stackTraceLimit is a V8 extension; guard for non-V8 runtimes.
  const originalLimit = (Error as { stackTraceLimit?: number }).stackTraceLimit;
  try {
    if (originalLimit !== undefined) {
      (Error as { stackTraceLimit?: number }).stackTraceLimit = 30;
    }
    return new Error().stack ?? '';
  } finally {
    if (originalLimit !== undefined) {
      (Error as { stackTraceLimit?: number }).stackTraceLimit = originalLimit;
    }
  }
}

/**
 * Parses a raw Error.stack string into clean StackFrame objects,
 * filtering out noisy internal frames so only project call sites remain.
 */
export function parseStackTrace(rawStack: string): StackFrame[] {
  const lines = rawStack.split('\n').slice(1); // drop the "Error" header line

  return lines
    .map((line) => line.trim())
    .filter(
      (line) =>
        line.startsWith('at ') && !NOISE_PATTERNS.some((p) => p.test(line)),
    )
    .map((line) => {
      // Format 1:  at FunctionName (file:line:col)
      const withParens = /^at (.+?) \((.+):(\d+):(\d+)\)$/.exec(line);
      if (withParens) {
        return {
          fn: withParens[1],
          file: withParens[2],
          line: withParens[3],
          column: withParens[4],
        };
      }

      // Format 2:  at file:line:col
      const noParens = /^at (.+):(\d+):(\d+)$/.exec(line);
      if (noParens) {
        return {
          fn: '(anonymous)',
          file: noParens[1],
          line: noParens[2],
          column: noParens[3],
        };
      }

      return null;
    })
    .filter((frame): frame is StackFrame => frame !== null)
    .slice(0, 10); // keep at most 10 useful frames
}

/**
 * Formats a parsed StackFrame array back into a readable string,
 * shortening file paths to be relative to `src/`.
 */
export function formatStackFrames(frames: StackFrame[]): string {
  return frames
    .map(({ fn, file, line, column }) => {
      const shortFile = file.replace(/.*\/src\//, 'src/').replace(/\?.*$/, '');
      return `  at ${fn} (${shortFile}:${line}:${column})`;
    })
    .join('\n');
}

// ─── URL Filter Matching ──────────────────────────────────────────────────────

/**
 * Evaluates whether a given URL should be logged based on the configured filters.
 *
 * @returns `true` if the URL should be logged (i.e., passes the filter).
 *
 * Rules:
 * - If `filters` is empty or undefined → always log (pass-through).
 * - If any filter matches → log.
 * - String filters are substring-matched (case-sensitive).
 * - RegExp filters are tested against the full URL string.
 */
export function matchesUrlFilters(
  url: string,
  filters: DebuggerConfig['urlFilters'],
): boolean {
  // Empty filter list → log everything
  if (!filters || filters.length === 0) return true;

  return filters.some((filter) => {
    if (typeof filter === 'string') {
      return url.includes(filter);
    }
    // RegExp
    return filter.test(url);
  });
}

// ─── Trigger Extraction ───────────────────────────────────────────────────────

/**
 * Internal substrings used to identify and skip noise frames when extracting
 * the trigger function. Mirrors the intent of NOISE_PATTERNS but operates on
 * raw string matching for speed.
 */
const TRIGGER_SKIP_SUBSTRINGS: string[] = [
  'api-debugger',
  'szs-next-api-debugger',
  'openapi-fetch',
  'captureStackTrace',
  'onRequest',
  'coreFetch',
  'node_modules_next',
  'next/dist',
  'react-dom',
  'scheduler',
  '@tanstack',
  // ── Added for SSR: React internals & Turbopack/Next.js RSC markers ────────
  'node_modules/react/',
  'react/cjs',
  'react.production',
  'react.development',
  '[project]',
  '[app-router]',
  '[root-of-the-server]',
  '[turbopack]',
  '(rsc)/',
  'node:internal',
  'node:async_hooks',
  'processTicksAndRejections',
];

/**
 * Function names that look like infrastructure / HTTP-method entry points and
 * should not be surfaced as the "trigger".
 */
const TRIGGER_SKIP_NAMES: Set<string> = new Set([
  'Object.construct',
  'Object.GET',
  'Object.POST',
  'Object.PUT',
  'Object.PATCH',
  'Object.DELETE',
  'Object.HEAD',
  'Object.OPTIONS',
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
  'HEAD',
  'OPTIONS',
  'async',
  'eval',
  'Module.default',
  'AsyncFunction',
  '<anonymous>',
  'Promise.all',
]);

/**
 * Regex extracting a function name from a V8 stack frame.
 * Handles the optional `async ` qualifier V8 inserts on awaited frames, and
 * stops at the first whitespace or opening parenthesis.
 *
 * Examples matched:
 *   "    at async RootLayout (.../layout.tsx:170:...)"  → "RootLayout"
 *   "    at Object.GET (.../openapi-fetch/...)"          → "Object.GET"
 *   "    at getVrIdProfile (.../auth.ts:11:...)"         → "getVrIdProfile"
 */
const FRAME_FN_REGEX = /at (?:async )?([^\s(]+)/;

/**
 * Directories that typically identify project (user-land) code paths.
 * Used by the file-basename fallback below.
 */
const PROJECT_PATH_REGEX =
  /\/((?:app|src|features|components|hooks|providers|pages|lib|api)\/[^)\s:]+)/;

/**
 * Walks a raw Error.stack string and returns the first function name that
 * belongs to user-land code — skipping all internal Next.js / library frames.
 *
 * When no meaningful function name survives (common on SSR where Turbopack
 * strips names), falls back to the basename of the first project file
 * encountered in the stack (e.g. `layout.tsx`, `growthBookServer.ts`) so the
 * overlay always shows something useful instead of `Unknown`.
 *
 * @returns The extracted trigger label, or `'Unknown'` if nothing usable found.
 */
export function extractTrigger(rawStack: string): string {
  const lines = rawStack.split('\n');

  // ── Pass 1: look for a named user-land function ─────────────────────────────
  for (const line of lines) {
    if (!line.includes('at ')) continue;
    if (TRIGGER_SKIP_SUBSTRINGS.some((s) => line.includes(s))) continue;

    const match = FRAME_FN_REGEX.exec(line);
    if (!match) continue;

    const name = match[1];
    if (!name || name.length < 2) continue;
    if (TRIGGER_SKIP_NAMES.has(name)) continue;

    return name;
  }

  // ── Pass 2: fallback — first project file basename ──────────────────────────
  for (const line of lines) {
    if (!line.includes('at ')) continue;
    if (TRIGGER_SKIP_SUBSTRINGS.some((s) => line.includes(s))) continue;

    const fileMatch = PROJECT_PATH_REGEX.exec(line);
    if (!fileMatch) continue;

    const segments = fileMatch[1].split('/');
    const basename = segments[segments.length - 1];
    if (basename) return basename.replace(/\?.*$/, '');
  }

  return 'Unknown';
}
